import React, { useEffect, useMemo, useState } from 'react'
import api from '../../utils/api'

/**
 * CreateInvoiceModal — popup form for creating a new invoice on a case.
 * Three steps: party selection → details → preview & send.
 */

function fmtMoney(n) {
    const v = Number(n || 0)
    return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function genInvoiceNumber() {
    const yr = new Date().getFullYear()
    const rand = String(Math.floor(Math.random() * 900) + 100)
    return `INV-${yr}-${rand}`
}

function defaultDueDate() {
    const d = new Date()
    d.setDate(d.getDate() + 14)
    return d.toISOString().split('T')[0]
}

export default function CreateInvoiceModal({ isOpen, onClose, caseData, onSuccess, onToast }) {
    const [step, setStep] = useState(1)
    const [party, setParty] = useState('both')
    const [invoiceNumber, setInvoiceNumber] = useState('')
    const [dueDate, setDueDate] = useState('')
    const [items, setItems] = useState([{ description: '', hours: 0, rate: 0 }])
    const [notes, setNotes] = useState('')
    const [includeProcessingFee, setIncludeProcessingFee] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        setInvoiceNumber(genInvoiceNumber())
        setDueDate(defaultDueDate())
        setItems([{ description: '', hours: 0, rate: 0 }])
        setNotes('')
        setIncludeProcessingFee(false)
        setStep(1)
        setParty('both')
    }, [isOpen])

    const itemTotal = useMemo(() => items.reduce((s, it) => s + Number(it.hours || 0) * Number(it.rate || 0), 0), [items])
    const processingFee = includeProcessingFee ? Math.round(itemTotal * 0.022 * 100) / 100 + 0.30 : 0
    const grandTotal = itemTotal + processingFee

    const updateItem = (i, patch) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, ...patch } : it))
    const addItem = () => setItems((p) => [...p, { description: '', hours: 0, rate: 0 }])
    const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i))

    const handleSave = async (sendNow = true) => {
        if (!caseData?.id) return
        try {
            setSubmitting(true)

            // BE requires case_id + client_id + mediator_id per invoice row.
            // Map UI "party" choice to the actual client_ids the BE needs.
            const pid = caseData.plaintiff?.client_id || caseData.plaintiff?.clientId
            const did = caseData.defendant?.client_id || caseData.defendant?.clientId
            const mediatorUserId = caseData.mediator?.user_id || caseData.mediator?.userId || caseData.mediatorId
            const clientIds = party === 'plaintiff' ? [pid].filter(Boolean)
                : party === 'defendant' ? [did].filter(Boolean)
                : [pid, did].filter(Boolean)

            if (clientIds.length === 0) {
                onToast?.({ type: 'error', message: 'No party available for this case' })
                setSubmitting(false)
                return
            }

            const lineItems = items
                .filter((it) => it.description || it.hours || it.rate)
                .map((it) => ({
                    description: it.description,
                    quantity: Number(it.hours || 1),
                    unit_price: Number(it.rate || 0),
                    amount: Number(it.hours || 0) * Number(it.rate || 0),
                }))

            // One POST per party (BE writes one payment row per case+client).
            // Use `allSettled` so a per-party failure (e.g., "already exists
            // and is paid") doesn't abort the other party's creation.
            const buildBody = (cid) => ({
                case_id: caseData.id,
                client_id: cid,
                mediator_id: mediatorUserId,
                invoice_number: invoiceNumber || undefined,
                invoice_customer_name: cid === pid ? caseData.plaintiff?.name : caseData.defendant?.name,
                invoice_emails: cid === pid ? [caseData.plaintiff?.email].filter(Boolean) : [caseData.defendant?.email].filter(Boolean),
                // Send `due_date` only when the user actually picked one —
                // an empty string makes the BE Stripe step compute NaN days
                // and fail. Backend defaults to 30 days when omitted.
                ...(dueDate ? { due_date: dueDate } : {}),
                items: lineItems,
                amount: itemTotal,
                grand_total: grandTotal,
                isFixedPriceInvoice: true,
                isProcessingFeeAddedInLineItem: includeProcessingFee,
                status: sendNow ? 'pending' : 'draft',
            })
            const results = await Promise.allSettled(
                clientIds.map((cid) => api.post('/billing', buildBody(cid)))
            )
            const fulfilled = results.filter((r) => r.status === 'fulfilled')
            const rejected = results.filter((r) => r.status === 'rejected')

            if (fulfilled.length === clientIds.length) {
                // All succeeded. Await the parent's refresh so the
                // FinanceTable has the new row in context BEFORE we close
                // the modal — otherwise users sometimes see an empty
                // Finances section because the close + refresh ran out
                // of order.
                onToast?.({ type: 'success', message: sendNow ? 'Invoice created' : 'Draft saved' })
                if (onSuccess) await onSuccess()
                onClose()
            } else if (fulfilled.length > 0) {
                // Partial success. Same await — the partially-saved row(s)
                // need to be in caseData by the time the modal disappears.
                const firstErr = rejected[0]?.reason?.response?.data?.message || 'Some invoices could not be created'
                onToast?.({ type: 'warning', message: `${fulfilled.length} of ${clientIds.length} invoice${clientIds.length > 1 ? 's' : ''} created. ${firstErr}` })
                if (onSuccess) await onSuccess()
                onClose()
            } else {
                // All failed — surface the BE error message verbatim.
                const msg = rejected[0]?.reason?.response?.data?.message || 'Failed to save invoice'
                onToast?.({ type: 'error', message: msg })
            }
        } catch (err) {
            onToast?.({ type: 'error', message: err?.response?.data?.message || 'Failed to save invoice' })
        } finally {
            setSubmitting(false)
        }
    }

    if (!isOpen) return null

    const partyName = party === 'plaintiff' ? caseData?.plaintiff?.name : (party === 'defendant' ? caseData?.defendant?.name : 'Both Parties')

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={() => !submitting && onClose()}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Create Invoice</h3>
                        <p className="text-xs text-gray-500">{caseData?.caseName} · #{caseData?.caseNumber}</p>
                    </div>
                    <div className="flex gap-1">
                        {[1, 2, 3].map((n) => <div key={n} className={`w-8 h-1.5 rounded-full ${step >= n ? 'bg-teal-500' : 'bg-gray-200'}`} />)}
                    </div>
                </div>
                <div className="overflow-y-auto flex-1">
                    {step === 1 && (
                        <div className="p-6 space-y-4">
                            <h3 className="text-sm font-semibold text-gray-700">Who is this invoice for?</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {[
                                    { k: 'plaintiff', label: 'Plaintiff', sub: caseData?.plaintiff?.name },
                                    { k: 'defendant', label: 'Defendant', sub: caseData?.defendant?.name },
                                    { k: 'both', label: 'Both Parties', sub: 'One invoice each' },
                                ].map((opt) => (
                                    <button type="button" key={opt.k} onClick={() => setParty(opt.k)}
                                        className={`text-left p-4 border-2 rounded-lg transition-colors ${party === opt.k ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                        <div className="font-medium text-gray-900">{opt.label}</div>
                                        <div className="text-xs text-gray-500 mt-1">{opt.sub || '—'}</div>
                                    </button>
                                ))}
                            </div>
                            <div className="flex justify-end pt-4">
                                <button type="button" onClick={() => setStep(2)} className="px-4 py-2 bg-[#173336] text-white rounded-md text-sm font-medium">Next →</button>
                            </div>
                        </div>
                    )}
                    {step === 2 && (
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number</label>
                                    <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
                                    <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-medium text-gray-700">Line Items</label>
                                    <button type="button" onClick={addItem} className="text-xs text-teal-600 hover:text-teal-700 font-medium">+ Add Item</button>
                                </div>
                                <div className="space-y-2">
                                    {items.map((it, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                            <input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} placeholder="Description" className="col-span-6 px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                                            <input type="number" value={it.hours} onChange={(e) => updateItem(i, { hours: e.target.value })} placeholder="Hours" className="col-span-2 px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                                            <input type="number" value={it.rate} onChange={(e) => updateItem(i, { rate: e.target.value })} placeholder="Rate" className="col-span-2 px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                                            <div className="col-span-1 text-sm text-gray-700 font-medium">{fmtMoney((it.hours || 0) * (it.rate || 0))}</div>
                                            <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-red-500 hover:text-red-700 text-xs">×</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={includeProcessingFee} onChange={(e) => setIncludeProcessingFee(e.target.checked)} className="w-4 h-4" />
                                <span>Include Stripe processing fee (2.2% + $0.30) on invoice</span>
                            </label>
                            <div className="flex justify-between items-center pt-4 border-t">
                                <button type="button" onClick={() => setStep(1)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm">← Back</button>
                                <button type="button" onClick={() => setStep(3)} className="px-4 py-2 bg-[#173336] text-white rounded-md text-sm font-medium">Preview →</button>
                            </div>
                        </div>
                    )}
                    {step === 3 && (
                        <div className="p-6 space-y-4">
                            <div className="bg-gray-50 rounded-lg p-5 space-y-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-sm font-semibold">{partyName}</h3>
                                        <p className="text-xs text-gray-500">Invoice #{invoiceNumber}</p>
                                    </div>
                                    <p className="text-xs text-gray-500">Due {dueDate}</p>
                                </div>
                                <table className="w-full text-sm">
                                    <thead className="text-left text-xs text-gray-500 uppercase border-b">
                                        <tr><th className="py-1">Description</th><th>Hours</th><th>Rate</th><th className="text-right">Amount</th></tr>
                                    </thead>
                                    <tbody>
                                        {items.filter((it) => it.description || it.hours || it.rate).map((it, i) => (
                                            <tr key={i} className="border-b last:border-b-0">
                                                <td className="py-2">{it.description || '—'}</td>
                                                <td>{it.hours}</td>
                                                <td>{fmtMoney(it.rate)}</td>
                                                <td className="text-right">{fmtMoney((it.hours || 0) * (it.rate || 0))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div className="text-right space-y-1 pt-2 border-t">
                                    <div className="text-sm">Subtotal: {fmtMoney(itemTotal)}</div>
                                    {includeProcessingFee && <div className="text-sm">Processing Fee: {fmtMoney(processingFee)}</div>}
                                    <div className="text-base font-semibold">Total: {fmtMoney(grandTotal)}</div>
                                </div>
                                {notes && <div className="text-xs text-gray-600 pt-2 border-t">{notes}</div>}
                            </div>
                            <div className="flex justify-between items-center pt-4 border-t">
                                <button type="button" onClick={() => setStep(2)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm">← Back</button>
                                <div className="flex gap-2">
                                    <button type="button" disabled={submitting} onClick={() => handleSave(false)} className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-md text-sm disabled:opacity-50">Save as Draft</button>
                                    <button type="button" disabled={submitting} onClick={() => handleSave(true)} className="px-4 py-2 bg-[#173336] text-white rounded-md text-sm font-medium disabled:opacity-50">
                                        {submitting ? 'Sending…' : 'Send Invoice'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
